/* eslint-disable @typescript-eslint/no-explicit-any */
import { ReactAtom } from "@mongez/react-atom";
import { useNavigation } from "@remix-run/react";
import { useEffect, useId, useRef } from "react";
import { Button } from "~/design-system/components";
import { CloseIcon } from "~/design-system/components/icons";
import { cn } from "~/design-system/lib/utils";
import { PortalActions } from "~/shared/portals-atoms";

export const widthMap = {
  sm: "w-[500px]",
  md: "w-[800px]",
  lg: "w-full",
  xl: "w-[1200px]",
  "fit-content": "w-fit",
  "full-screen": "w-full h-full max-h-full",
};

const radiusMap = {
  none: "rounded-none",
  sm: "rounded-sm",
  md: "rounded-md",
  lg: "rounded-lg",
  xl: "rounded-xl",
  "2xl": "rounded-2xl",
  "3xl": "rounded-3xl",
};

export type PortalProps = {
  portal: ReactAtom<any, PortalActions>;
  clickOutside?: boolean;
  closeButton?: boolean;
  esc?: boolean;
  width?: keyof typeof widthMap | number;
  radius?: keyof typeof radiusMap;
  overlay?: boolean;
  zIndex?: number;
  className?: string;
  children: React.ReactNode;
  keepMounted?: boolean;
  classes?: {
    root?: string;
    overlay?: string;
    content?: string;
    closeButton?: string;
  };
};

export function Portal({
  zIndex = 1000,
  portal,
  children,
  classes = {},
  overlay = true,
  clickOutside = true,
  closeButton,
  width = "lg",
  keepMounted = false,
  // radius = "3xl",
}: PortalProps) {
  const portalData = portal.useValue();

  const opened = portal.useOpened();
  const ref = useRef<HTMLDivElement>();
  const childrenRef = useRef<HTMLDivElement>();
  const navigation = useNavigation();

  const id = useId();

  useEffect(() => {
    if (!opened || navigation.state !== "loading") return;

    // close on navigation
    portal.close();
  }, [navigation.state, opened, portal]);

  useEffect(() => {
    if (opened) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
  }, [opened]);

  useEffect(() => {
    if (opened && childrenRef.current) {
      childrenRef.current.scrollTop = 0;
    }
  }, [opened]);

  const closeOverlay = () => {
    if (!clickOutside) return;
    portal.close();
  };

  if (!opened && !keepMounted) return null;

  return (
    <div
      style={{
        zIndex,
      }}
      className={cn(
        "portal fixed inset-0 flex animate-fade-in items-center justify-center",
        opened === false && "hidden",
        classes.root,
        width === "full-screen" && "p-0",
        portalData.closeAnimation && "animate-fade-out",
      )}
      id={`portal-${id}`}
      ref={ref as any}
    >
      <>
        {overlay && (
          <div
            onClick={closeOverlay}
            aria-hidden="true"
            className={cn(
              `fixed inset-0 z-50 h-full w-full bg-black bg-opacity-70 transition-all`,
              classes.overlay,
            )}
          />
        )}
        <div
          className={cn(
            "portal-content relative top-2 z-50 mx-auto mb-8 mt-0 max-h-[95%] max-w-[90%] gap-6 overflow-auto rounded-xl bg-white p-6",
            typeof width === "number" ? `w-[${width}px]` : widthMap[width],
            classes.content,
          )}
          ref={childrenRef as any}
        >
          {closeButton && (
            <div className="flex w-full justify-end">
              <Button
                buttonColor="transparent"
                variant="outlined"
                className="w-fit rounded-full border border-gray-200 p-1 md:p-2"
                onClick={portal.close}
              >
                <CloseIcon />
              </Button>
            </div>
          )}
          {keepMounted === true ? children : opened && children}
        </div>
      </>
    </div>
  );
}
